import { useState, useEffect } from 'react';
import { validateResetToken, resetPassword } from '../../service/AuthService';

interface Props {
    token: string;
    onClose: () => void;
    onLoginOpen: () => void;
}

type TokenState = 'validating' | 'valid' | 'invalid';
type SubmitState = 'idle' | 'loading' | 'success';

const PASSWORD_RULES = [
    { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
    { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
    { label: 'One number', test: (p: string) => /[0-9]/.test(p) },
    { label: 'One special character', test: (p: string) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(p) },
];

const ResetPasswordModal = ({ token, onClose, onLoginOpen }: Props) => {
    const [tokenState, setTokenState] = useState<TokenState>('validating');
    const [tokenType, setTokenType] = useState<'RESET' | 'SET_PASSWORD'>('RESET');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitState, setSubmitState] = useState<SubmitState>('idle');
    const [error, setError] = useState('');

    const isFirstTime = tokenType === 'SET_PASSWORD';

    useEffect(() => {
        validateResetToken(token)
            .then((res) => {
                setTokenType(res.type === 'SET_PASSWORD' ? 'SET_PASSWORD' : 'RESET');
                setTokenState('valid');
            })
            .catch(() => setTokenState('invalid'));
    }, [token]);

    const allRulesPassed = PASSWORD_RULES.every(r => r.test(newPassword));
    const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;
    const canSubmit = allRulesPassed && passwordsMatch && submitState === 'idle';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitState('loading');
        setError('');
        try {
            await resetPassword(token, newPassword);
            setSubmitState('success');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to reset password. Please try again.');
            setSubmitState('idle');
        }
    };

    const inputCls = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md relative">
                <button
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
                    onClick={onClose}
                >
                    <i className="fas fa-times text-lg"></i>
                </button>

                {/* Validating */}
                {tokenState === 'validating' && (
                    <div className="text-center py-8">
                        <div className="loader border-t-4 border-blue-500 rounded-full w-10 h-10 animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-500 text-sm">Validating your reset link…</p>
                    </div>
                )}

                {/* Invalid token */}
                {tokenState === 'invalid' && (
                    <div className="text-center py-4">
                        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-exclamation-triangle text-red-500 text-2xl"></i>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">Link expired</h2>
                        <p className="text-gray-500 text-sm mb-6">
                            This password reset link is invalid or has expired. Reset links are valid for 1 hour.
                        </p>
                        <button
                            onClick={onClose}
                            className="gradient-bg text-white px-6 py-2 rounded-lg font-medium hover:opacity-90 transition text-sm"
                        >
                            Request a new link
                        </button>
                    </div>
                )}

                {/* Valid — reset form */}
                {tokenState === 'valid' && submitState !== 'success' && (
                    <div>
                        <h2 className="text-2xl font-bold mb-2 text-gray-800">
                            {isFirstTime ? 'Welcome to StockEasy!' : 'Set new password'}
                        </h2>
                        <p className="text-gray-500 text-sm mb-6">
                            {isFirstTime
                                ? 'Your account is ready. Set a password to get started.'
                                : 'Choose a strong new password for your account.'}
                        </p>

                        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">New Password</label>
                                <input
                                    type="password"
                                    className={inputCls}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    disabled={submitState === 'loading'}
                                    autoComplete="new-password"
                                />
                            </div>

                            {/* Password rules */}
                            {newPassword.length > 0 && (
                                <ul className="space-y-1">
                                    {PASSWORD_RULES.map((rule) => (
                                        <li key={rule.label} className={`flex items-center gap-2 text-xs ${rule.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                                            <i className={`fas ${rule.test(newPassword) ? 'fa-check-circle' : 'fa-circle'} text-xs`}></i>
                                            {rule.label}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div>
                                <label className="block text-gray-700 font-medium mb-1 text-sm">Confirm Password</label>
                                <input
                                    type="password"
                                    className={`${inputCls} ${confirmPassword.length > 0 ? (passwordsMatch ? 'border-green-400' : 'border-red-400') : ''}`}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={submitState === 'loading'}
                                    autoComplete="new-password"
                                />
                                {confirmPassword.length > 0 && !passwordsMatch && (
                                    <p className="text-red-500 text-xs mt-1">Passwords do not match.</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={!canSubmit}
                                className={`gradient-bg text-white px-6 py-3 rounded-lg font-medium transition w-full hover:opacity-90 ${!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {submitState === 'loading'
                                    ? (isFirstTime ? 'Setting…' : 'Resetting…')
                                    : (isFirstTime ? 'Set Password' : 'Reset Password')}
                            </button>
                        </form>
                    </div>
                )}

                {/* Success */}
                {submitState === 'success' && (
                    <div className="text-center py-4">
                        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-check text-green-500 text-2xl"></i>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">
                            {isFirstTime ? 'Password set!' : 'Password updated!'}
                        </h2>
                        <p className="text-gray-500 text-sm mb-6">
                            {isFirstTime
                                ? 'Your password has been set. You can now log in to your account.'
                                : 'Your password has been reset successfully.'}
                        </p>
                        <button
                            onClick={() => { onClose(); onLoginOpen(); }}
                            className="gradient-bg text-white px-6 py-2 rounded-lg font-medium hover:opacity-90 transition text-sm"
                        >
                            Click here to log in
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResetPasswordModal;
